import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import Alert from '@mui/material/Alert';
import Divider from '@mui/material/Divider';
import MuiButton from '@mui/material/Button';
import { toast } from '../../../../ui/toast';
import { Button } from '../../../../ui';
import LoadingSpinner from '../../../job_openings/create/ui/LoaderSpinner';
import { getPermissions } from '../../../../utils/helpers/auth';
import {
  getAuthSettings,
  putAuthSettings,
  testSso,
} from '../../../../utils/api_service/mfa_api';

const POLICIES: { value: string; label: string; help: string }[] = [
  { value: 'off', label: 'Off', help: 'Members sign in with a password only.' },
  { value: 'required_for_admins', label: 'Required for admins', help: 'Admins must use an authenticator app.' },
  { value: 'required_for_all', label: 'Required for everyone', help: 'All members (not candidates) must use MFA.' },
];

export const SecuritySettings: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(true);
  const [mfaPolicy, setMfaPolicy] = useState('off');
  const [sso, setSso] = useState<any>(null);
  const [ssoForm, setSsoForm] = useState<any>({
    provider: 'google',
    issuer: '',
    clientId: '',
    clientSecret: '',
    allowedEmailDomains: '',
    ssoSatisfiesMfa: false,
    enabled: false,
  });
  const [redirectUri, setRedirectUri] = useState('');

  const token = localStorage.getItem('token');
  const canEdit = token ? getPermissions(token).includes('admin:security') : false;

  useEffect(() => {
    getAuthSettings()
      .then((r) => r.json())
      .then((d) => {
        if (d && typeof d.mfaPolicy === 'string') {
          setMfaPolicy(d.mfaPolicy);
          setPasswordLoginEnabled(Boolean(d.passwordLoginEnabled));
          if (d.sso) {
            setSso(d.sso);
            setRedirectUri(d.sso.redirectUri || '');
            setSsoForm({
              provider: d.sso.provider || 'google',
              issuer: d.sso.issuer || '',
              clientId: d.sso.clientId || '',
              clientSecret: '',
              allowedEmailDomains: (d.sso.allowedEmailDomains || []).join(', '),
              ssoSatisfiesMfa: d.sso.ssoSatisfiesMfa === true,
              enabled: !!d.sso.enabled,
            });
          }
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const buildSso = () => ({
    provider: ssoForm.provider,
    issuer: ssoForm.issuer.trim(),
    clientId: ssoForm.clientId.trim(),
    ...(ssoForm.clientSecret ? { clientSecret: ssoForm.clientSecret } : {}),
    allowedEmailDomains: ssoForm.allowedEmailDomains
      .split(',')
      .map((x: string) => x.trim())
      .filter(Boolean),
    ssoSatisfiesMfa: ssoForm.ssoSatisfiesMfa,
    enabled: ssoForm.enabled,
  });

  const saveSso = async (enable: boolean) => {
    const body: any = { passwordLoginEnabled, mfaPolicy, sso: { ...buildSso(), enabled: enable } };
    const res = await putAuthSettings(body);
    const d = await res.json().catch(() => ({}));
    if (res.ok) {
      toast.success('SSO settings saved', { position: 'bottom-center' });
      setSso(d.sso);
      setSsoForm((f: any) => ({ ...f, enabled: !!d.sso?.enabled }));
    } else {
      toast.error(d.error || 'Could not save SSO. Test the connection before enabling.', { position: 'bottom-center' });
    }
  };

  const runTest = async () => {
    const saved = await putAuthSettings({ passwordLoginEnabled, mfaPolicy, sso: { ...buildSso(), enabled: false } });
    if (saved.ok) {
      const sd = await saved.json().catch(() => ({}));
      setSso(sd.sso || null);
      setSsoForm((f: any) => ({ ...f, enabled: false }));
    }
    const res = await testSso();
    const d = await res.json().catch(() => ({}));
    if (d.ok) {
      toast.success('Connection verified — you can enable SSO now', { position: 'bottom-center' });
    } else {
      toast.error('Test failed: ' + (d.problems ? d.problems.join(', ') : d.error || 'unreachable'), { position: 'bottom-center' });
    }
  };

  const removeSso = async () => {
    await putAuthSettings({ passwordLoginEnabled, mfaPolicy, removeSso: true });
    setSso(null);
    setSsoForm({
      provider: 'google',
      issuer: '',
      clientId: '',
      clientSecret: '',
      allowedEmailDomains: '',
      ssoSatisfiesMfa: false,
      enabled: false,
    });
    toast.success('SSO removed', { position: 'bottom-center' });
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await putAuthSettings({ passwordLoginEnabled, mfaPolicy });
      if (res.ok) {
        toast.success('Security settings saved', { position: 'bottom-center' });
      } else if (res.status === 401 || res.status === 403) {
        toast.error('You do not have permission to change security settings.', { position: 'bottom-center' });
      } else {
        toast.error('Could not save security settings.', { position: 'bottom-center' });
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: 350, gap: 1.5 }}>
        <LoadingSpinner label='' />
        <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Loading Security Settings...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5, maxWidth: 720, p: 0.5 }}>
      {/* MFA Section */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Multi-factor authentication
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
          Candidates and one-off assessment / interview links are never affected by this policy.
        </Typography>

        <RadioGroup value={mfaPolicy} onChange={(e) => setMfaPolicy(e.target.value)}>
          <Stack spacing={1.5}>
            {POLICIES.map((p) => (
              <Paper
                key={p.value}
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: '8px',
                  borderColor: mfaPolicy === p.value ? 'primary.main' : 'divider',
                  bgcolor: mfaPolicy === p.value ? 'primary.50' : 'background.paper',
                  cursor: canEdit ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  '&:hover': canEdit ? { borderColor: 'primary.main' } : {},
                }}
                onClick={() => canEdit && setMfaPolicy(p.value)}
              >
                <FormControlLabel
                  value={p.value}
                  disabled={!canEdit}
                  control={<Radio size="small" />}
                  label={
                    <Box sx={{ ml: 0.5 }}>
                      <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'text.primary' }}>
                        {p.label}
                      </Typography>
                      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                        {p.help}
                      </Typography>
                    </Box>
                  }
                  sx={{ width: '100%', m: 0, alignItems: 'flex-start' }}
                />
              </Paper>
            ))}
          </Stack>
        </RadioGroup>
      </Box>

      {/* Password sign-in */}
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
          Password sign-in
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              size="small"
              checked={passwordLoginEnabled}
              disabled={!canEdit}
              onChange={(e) => setPasswordLoginEnabled(e.target.checked)}
            />
          }
          label={<Typography sx={{ fontSize: '0.875rem', color: 'text.primary' }}>Allow members to sign in with a password</Typography>}
        />
      </Box>

      {/* SSO Section */}
      {canEdit && (
        <Box sx={{ pt: 1 }}>
          <Divider sx={{ mb: 3 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 0.5 }}>
            Single sign-on (SSO)
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2.5 }}>
            OpenID Connect. Register the redirect URI below in your identity provider.
          </Typography>

          {redirectUri && (
            <Box sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.secondary', mb: 0.5 }}>
                Redirect URI
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: '6px',
                  bgcolor: 'action.hover',
                  fontFamily: 'monospace',
                  fontSize: '0.75rem',
                  wordBreak: 'break-all',
                }}
              >
                {redirectUri}
              </Paper>
            </Box>
          )}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <FormControl fullWidth size="small">
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Provider
              </Typography>
              <Select
                value={ssoForm.provider}
                onChange={(e) => setSsoForm({ ...ssoForm, provider: e.target.value })}
                sx={{ borderRadius: '8px' }}
              >
                <MenuItem value="google">Google Workspace</MenuItem>
                <MenuItem value="microsoft">Microsoft Entra</MenuItem>
                <MenuItem value="okta">Okta</MenuItem>
                <MenuItem value="generic">Generic OIDC</MenuItem>
              </Select>
            </FormControl>

            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Issuer URL
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={ssoForm.issuer}
                onChange={(e) => setSsoForm({ ...ssoForm, issuer: e.target.value })}
                placeholder="https://accounts.google.com"
                InputProps={{ sx: { borderRadius: '8px' } }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Client ID
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={ssoForm.clientId}
                onChange={(e) => setSsoForm({ ...ssoForm, clientId: e.target.value })}
                InputProps={{ sx: { borderRadius: '8px' } }}
              />
            </Box>

            <Box>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Client secret
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="password"
                value={ssoForm.clientSecret}
                onChange={(e) => setSsoForm({ ...ssoForm, clientSecret: e.target.value })}
                placeholder={sso?.clientSecret || 'Enter to set/replace'}
                InputProps={{ sx: { borderRadius: '8px' } }}
              />
            </Box>

            <Box sx={{ gridColumn: { sm: 'span 2' } }}>
              <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                Allowed email domains (comma-separated)
              </Typography>
              <TextField
                fullWidth
                size="small"
                value={ssoForm.allowedEmailDomains}
                onChange={(e) => setSsoForm({ ...ssoForm, allowedEmailDomains: e.target.value })}
                placeholder="acme.com, acme.co"
                InputProps={{ sx: { borderRadius: '8px' } }}
              />
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={ssoForm.ssoSatisfiesMfa}
                onChange={(e) => setSsoForm({ ...ssoForm, ssoSatisfiesMfa: e.target.checked })}
              />
            }
            label={<Typography sx={{ fontSize: '0.875rem' }}>SSO counts as multi-factor (the IdP enforces it)</Typography>}
            sx={{ mt: 1.5 }}
          />

          <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" sx={{ mt: 2.5 }}>
            <Button variant="secondary" onClick={runTest} label="Test connection" />
            {ssoForm.enabled ? (
              <Button onClick={() => saveSso(false)} label="Disable SSO" />
            ) : (
              <Button onClick={() => saveSso(true)} label="Save and enable SSO" />
            )}
            <MuiButton
              variant="text"
              size="small"
              onClick={() => saveSso(ssoForm.enabled)}
              sx={{ textTransform: 'none', color: 'text.secondary', fontSize: '0.8125rem' }}
            >
              Save without changing status
            </MuiButton>
            {sso && (
              <MuiButton
                variant="text"
                size="small"
                onClick={removeSso}
                sx={{ textTransform: 'none', color: 'error.main', fontSize: '0.8125rem' }}
              >
                Remove SSO
              </MuiButton>
            )}
          </Stack>
        </Box>
      )}

      {/* Save Button / Notice */}
      <Box sx={{ pt: 1 }}>
        {canEdit ? (
          <Button onClick={save} disabled={saving} label={saving ? 'Saving…' : 'Save changes'} />
        ) : (
          <Alert severity="warning" sx={{ borderRadius: '8px' }}>
            These settings are read-only for you. Only members with the <strong>Security</strong> permission can change them.
            Ask an administrator to grant it from Roles.
          </Alert>
        )}
      </Box>
    </Box>
  );
};
